import { Component, ChangeDetectionStrategy, input, output, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ZardCommandModule } from '../zard/command/command.module';
import type { ZardCommandOption } from '../zard/command/command.component';

export enum DateGroup {
    TODAY = 'Today ',
    LAST_7_DAYS = 'Last 7 days',
    OLDER = 'Older',
}

export interface CommandItem {
    id: string;
    label: string;
    icon?: string;
    timestamp?: string;
    tooltipDate?: string;
    dateGroup?: DateGroup;
    [key: string]: any;
}

export interface CommandItemGroup {
    label: string;
    items: CommandItem[];
}

@Component({
    selector: 'app-command',
    standalone: true,
    imports: [CommonModule, ZardCommandModule],
    templateUrl: './command.component.html',
    styleUrls: ['./command.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommandComponent {
    /**
     * Placeholder text for the search input
     */
    placeholder = input<string>('Search...');

    /**
     * Array of items to display in the command list
     */
    items = input<CommandItem[]>([]);

    /**
     * Loading state for initial load
     */
    loading = input<boolean>(false);

    /**
     * Empty state message when no results are found
     */
    emptyMessage = input<string>('No results found.');

    /**
     * Loading more message when fetching additional results
     */
    loadingMoreMessage = input<string>('Loading more...');

    /**
     * Group label - can be static or dynamic based on search
     */
    groupLabel = input<string>('Results');

    /**
     * Search results group label (when user is searching)
     */
    searchGroupLabel = input<string>('Search Results');

    /**
     * Default group label (when no search value)
     */
    defaultGroupLabel = input<string>('');

    /**
     * Whether to show group label dynamically based on search
     */
    dynamicGroupLabel = input<boolean>(true);

    /**
     * Whether more results are being fetched
     */
    isFetchingMore = input<boolean>(false);

    /**
     * Whether to disable the internal filter
     */
    disableInternalFilter = input<boolean>(true);

    /**
     * Current search value
     */
    searchValue = input<string>('');

    /**
     * Whether to show the "Loading more..." indicator
     */
    showLoadingMore = input<boolean>(true);

    /**
     * Whether to show the close button in the input
     */
    showCloseButton = input<boolean>(true);

    /**
     * Whether to group items by date (Today / Last 7 days / Older)
     */
    showGroups = input<boolean>(false);

    /**
     * Event emitted when an item is selected
     */
    itemSelected = output<CommandItem>();

    /**
     * Event emitted when search value changes
     */
    searchValueChange = output<string>();

    /**
     * Event emitted when close button is clicked
     */
    closeClicked = output<void>();

    /**
     * Get the group label based on search state
     */
    computedGroupLabel = computed(() => {
        if (!this.dynamicGroupLabel()) {
            return this.groupLabel();
        }
        return this.searchValue().trim() ? this.searchGroupLabel() : this.defaultGroupLabel();
    });

    /**
     * Items split into date groups — only populated when showGroups is true
     */
    groupedItems = computed((): CommandItemGroup[] | null => {
        if (!this.showGroups()) return null;

        const buckets = new Map<DateGroup, CommandItem[]>([
            [DateGroup.TODAY, []],
            [DateGroup.LAST_7_DAYS, []],
            [DateGroup.OLDER, []],
        ]);

        for (const item of this.items()) {
            const group = item.dateGroup ?? DateGroup.OLDER;
            buckets.get(group)!.push(item);
        }

        const groups: CommandItemGroup[] = [];
        for (const [label, items] of buckets) {
            if (items.length > 0) groups.push({ label, items });
        }

        return groups.length > 0 ? groups : null;
    });

    /**
     * Handle item selection from command
     */
    onItemSelected(option: ZardCommandOption): void {
        const selectedItem = this.items().find(item => item.id === option.value);
        if (selectedItem) {
            this.itemSelected.emit(selectedItem);
        }
    }

    /**
     * Handle search value change
     */
    onSearchValueChange(value: string): void {
        this.searchValueChange.emit(value);
    }

    /**
     * Handle close button click
     */
    onCloseButtonClick(): void {
        this.closeClicked.emit();
    }
}

